import { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar, Box, Button, Chip, Collapse, IconButton, InputBase, Paper,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VerifiedIcon from '@mui/icons-material/Verified';
import UndoIcon from '@mui/icons-material/Undo';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import * as Parse from "parse";
import dayjs from 'dayjs';
import { tokens } from "../theme";
import { hasReportingAccess } from "../common/permissions";
import AccessDenied from "./AccessDenied";

const FETCH_LIMIT = 1000;
// Same default and same choices as the quotations table, so moving between the two
// screens does not mean relearning the footer.
const DEFAULT_ROWS_PER_PAGE = 20;
// Search, period and sort all sit on one line, so they share one height.
const CONTROL_HEIGHT = 36;
const ROWS_PER_PAGE_OPTIONS = [20, 50, 100, 200];
// A deposit on a big order touches dozens of parts. Showing a handful and offering
// the rest keeps one action to roughly one screen row.
const PARTS_SHOWN = 3;
// Typing is the fastest way to find something, so the search runs itself shortly
// after somebody stops typing instead of waiting for Enter to be discovered.
const SEARCH_DEBOUNCE_MS = 450;

// Named the way somebody would ask for it, and matched here in the browser so the
// stock that moved always stays attached to the action that moved it. The short
// label is what fits on a filter button; the long one explains it on hover.
const VIEWS = [
  { value: '', short: 'Everything', label: 'Everything', matches: () => true },
  { value: 'stock', short: 'Stock moved', label: 'Only where stock moved', matches: (e) => e.movements.length > 0 },
  { value: 'status', short: 'Status changes', label: 'Order status changes', matches: (e) => e.action === 'status-change' },
  { value: 'new', short: 'New & revisions', label: 'New orders and revisions', matches: (e) => e.action === 'order-created' || e.action === 'revision-created' },
  { value: 'final', short: 'Finalising', label: 'Finalising and un-finalising', matches: (e) => e.action === 'finalized' || e.action === 'unfinalized' },
  { value: 'items', short: 'Items edited', label: 'Items edited on an order', matches: (e) => e.action === 'items-changed' },
  { value: 'deleted', short: 'Deletions', label: 'Orders and revisions deleted', matches: (e) => e.action === 'order-deleted' || e.action === 'revision-deleted' },
];

const PERIODS = [
  { value: 'today', short: 'Today', label: 'Today', days: 0 },
  { value: '7', short: '7 days', label: 'Last 7 days', days: 7 },
  { value: '30', short: '30 days', label: 'Last 30 days', days: 30 },
  { value: '90', short: '3 months', label: 'Last 3 months', days: 90 },
  { value: 'all', short: 'All time', label: 'Everything on record', days: null },
];

const DEFAULT_PERIOD = '7';

// Each action gets a shape the eye can sort without reading the words.
const ACTION_STYLE = {
  'status-change': { Icon: SwapHorizIcon, tone: 'info', word: 'Status changed' },
  'order-created': { Icon: AddCircleOutlineIcon, tone: 'info', word: 'New order' },
  'revision-created': { Icon: AddCircleOutlineIcon, tone: 'info', word: 'New revision' },
  'finalized': { Icon: VerifiedIcon, tone: 'in', word: 'Finalised' },
  'unfinalized': { Icon: UndoIcon, tone: 'info', word: 'Un-finalised' },
  'items-changed': { Icon: EditOutlinedIcon, tone: 'info', word: 'Items edited' },
  'order-deleted': { Icon: DeleteOutlineIcon, tone: 'out', word: 'Order deleted' },
  'revision-deleted': { Icon: DeleteOutlineIcon, tone: 'out', word: 'Revision deleted' },
  'stock-out': { Icon: ArrowDownwardIcon, tone: 'out', word: 'Stock out' },
  'stock-in': { Icon: ArrowUpwardIcon, tone: 'in', word: 'Stock back in' },
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value.iso) return new Date(value.iso);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const timeOf = (value) => {
  const date = toDate(value);
  return date ? date.getTime() : 0;
};

const dayHeading = (date) => {
  const day = dayjs(date).startOf('day');
  const today = dayjs().startOf('day');
  if (day.isSame(today)) return 'Today';
  if (day.isSame(today.subtract(1, 'day'))) return 'Yesterday';
  return day.format('dddd D MMMM YYYY');
};

const relativeTime = (date) => {
  const minutes = Math.round((Date.now() - new Date(date).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 31) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
};

const signed = (value) => (value > 0 ? `+${value}` : `${value}`);

const initialsOf = (name) => (name || '')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((word) => word[0].toUpperCase())
  .join('');

// The same person keeps the same coloured initials down the whole list, which is what
// makes "who has been touching this order" readable at a glance.
const nameShade = (name) => {
  let total = 0;
  for (let index = 0; index < (name || '').length; index += 1) {
    total += name.charCodeAt(index);
  }
  return total;
};

const csvCell = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const ActivityLog = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === 'dark';
  const currentUser = Parse.User.current();
  const canView = hasReportingAccess(currentUser);

  const surface = colors.primary[400];
  const band = isDark ? colors.primary[500] : colors.grey[900];
  const trackBg = isDark ? colors.primary[500] : colors.grey[900];
  const cubeInk = isDark ? '#ffffff' : colors.grey[300];
  const thumbBg = isDark ? colors.primary[400] : '#ffffff';
  const line = isDark ? alpha(colors.grey[100], 0.12) : alpha(colors.grey[100], 0.14);

  const [entries, setEntries] = useState([]);
  // A fetch fires on mount, so the spinner is the honest first frame - starting at
  // false flashed "Nothing happened here" before the first row arrived.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hitLimit, setHitLimit] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);

  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [view, setView] = useState('');
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [newestFirst, setNewestFirst] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [expanded, setExpanded] = useState({});

  // Paging scrolls the rows, not the window, so every page has to start at its own
  // first row - otherwise page 2 opens halfway down.
  const scrollRef = useRef(null);
  const goToPage = (next) => {
    setPage(next);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const toneColor = (tone) => {
    if (tone === 'out') return colors.redAccent[isDark ? 400 : 500];
    if (tone === 'in') return colors.greenAccent[isDark ? 400 : 600];
    return colors.blueAccent[isDark ? 300 : 500];
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const selectedPeriod = PERIODS.find((option) => option.value === period);

      // Read through a cloud function rather than querying the class directly: the
      // log class does not exist until its first row is written, and the function is
      // also where one action's many rows get folded back into one.
      const results = await Parse.Cloud.run('getActivityLog', {
        sinceDays: selectedPeriod && selectedPeriod.days !== null ? selectedPeriod.days : undefined,
        search: appliedSearch || undefined,
        limit: FETCH_LIMIT,
      });

      setHitLimit(results.length === FETCH_LIMIT);

      // Nothing is decided here. Whether an order needs a look, including whether it is
      // holding units under two numbers at once, is settled on the server: it can see
      // the orders themselves, where that is a fact about right now.
      //
      // Counting stock-out rows in the browser is what used to produce the false
      // warning. A deposit that was released again leaves its row behind, so a parent
      // deposited, handed over to a revision and then deposited on that revision read
      // as two deductions when only one was ever outstanding.
      const loaded = results.map((entry) => ({
        ...entry,
        createdAt: toDate(entry.createdAt),
        movements: Array.isArray(entry.movements) ? entry.movements : [],
      }));

      setEntries(loaded);
      setLoadedAt(new Date());
      setPage(0);
      setExpanded({});
    } catch (e) {
      console.log('activity log error: ', e);
      setEntries([]);
      if (/invalid function/i.test(e?.message || '')) {
        setError('The activity log needs the API server restarted with the latest release.');
      } else {
        setError(e?.message || 'Could not load the activity log.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Typing settles into a search on its own. Enter still works for anyone who reaches
  // for it, and simply sets the same value sooner.
  useEffect(() => {
    const term = search.trim();
    if (term === appliedSearch) return undefined;
    const timer = setTimeout(() => setAppliedSearch(term), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, appliedSearch]);

  useEffect(() => {
    if (canView) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, appliedSearch, canView]);

  // "Needs a look" narrows everything else, so the view counts below are counted
  // inside it - a button that says 12 and then shows 3 is worse than no count.
  const inAttention = useMemo(
    () => (attentionOnly ? entries.filter((entry) => entry.needsAttention) : entries),
    [entries, attentionOnly],
  );

  const viewCounts = useMemo(() => {
    const counts = {};
    VIEWS.forEach((option) => {
      counts[option.value] = inAttention.filter(option.matches).length;
    });
    return counts;
  }, [inAttention]);

  const attentionCount = useMemo(
    () => entries.filter((entry) => entry.needsAttention).length,
    [entries],
  );

  const filtered = useMemo(() => {
    const selectedView = VIEWS.find((option) => option.value === view) ?? VIEWS[0];
    return inAttention.filter(selectedView.matches);
  }, [inAttention, view]);

  const ordered = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => (newestFirst
      ? timeOf(b.createdAt) - timeOf(a.createdAt)
      : timeOf(a.createdAt) - timeOf(b.createdAt)));
    return list;
  }, [filtered, newestFirst]);

  useEffect(() => {
    goToPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, attentionOnly, newestFirst]);

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= ordered.length) {
      goToPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordered.length, page, rowsPerPage]);

  const summary = useMemo(() => {
    let unitsIn = 0;
    let unitsOut = 0;
    let attention = 0;
    filtered.forEach((entry) => {
      entry.movements.forEach((movement) => {
        if (movement.delta >= 0) unitsIn += movement.delta;
        else unitsOut += Math.abs(movement.delta);
      });
      if (entry.needsAttention) attention += 1;
    });
    return { actions: filtered.length, unitsIn, unitsOut, attention };
  }, [filtered]);

  const periodLabel = (PERIODS.find((option) => option.value === period) ?? PERIODS[1]).label;
  const filtersTouched = !!appliedSearch || !!view || attentionOnly || period !== DEFAULT_PERIOD;

  const clearFilters = () => {
    setView('');
    setAttentionOnly(false);
    setSearch('');
    setAppliedSearch('');
    setPeriod(DEFAULT_PERIOD);
  };

  const exportCsv = () => {
    const headers = [
      'Date', 'Time', 'What happened', 'Details', 'Part', 'Change',
      'Was', 'Now', 'Order', 'Customer', 'Who', 'Needs a look',
    ];
    const lines = [headers.join(',')];
    // One line per part so the sheet can be filtered and totalled, with the action
    // repeated on each - which is what people do with these in Excel anyway.
    ordered.forEach((entry) => {
      const shared = [
        dayjs(entry.createdAt).format('YYYY-MM-DD'),
        dayjs(entry.createdAt).format('HH:mm'),
        entry.summary,
        entry.detail,
      ];
      const tail = [entry.quotationNumber, entry.clientName, entry.actorName,
        entry.needsAttention ? 'yes' : ''];

      if (entry.movements.length === 0) {
        lines.push([...shared, '', '', '', '', ...tail].map(csvCell).join(','));
        return;
      }
      entry.movements.forEach((movement) => {
        lines.push([
          ...shared,
          movement.partLabel,
          signed(movement.delta),
          movement.quantityBefore,
          movement.quantityAfter,
          ...tail,
        ].map(csvCell).join(','));
      });
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `activity_log_${dayjs().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const actionButton = (label, Icon, onClick, tone, disabled = false) => (
    <Button
      startIcon={<Icon />}
      onClick={onClick}
      disabled={disabled}
      sx={{
        px: 2, py: 1, borderRadius: 2, textTransform: 'none', fontWeight: 700,
        fontSize: '13px', color: colors.grey[100], backgroundColor: tone,
        '&:hover': { backgroundColor: alpha(tone, 0.85) },
        '&.Mui-disabled': { backgroundColor: alpha(tone, 0.35), color: alpha(colors.grey[100], 0.5) },
      }}
    >
      {label}
    </Button>
  );

  const notice = (tone, Icon, text) => (
    <Box
      display="flex"
      alignItems="flex-start"
      mt={1.5}
      p={1.25}
      sx={{
        borderRadius: 2,
        backgroundColor: alpha(tone, 0.1),
        border: `1px solid ${alpha(tone, 0.35)}`,
      }}
    >
      <Icon sx={{ fontSize: 18, color: tone, mr: 1, mt: '1px', flexShrink: 0 }} />
      <Typography fontSize="13px" color={colors.grey[100]}>{text}</Typography>
    </Box>
  );

  const tile = ({ label, value, hint, Icon, valueTone, active, onClick }) => (
    <Paper
      elevation={0}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      sx={{
        flex: '1 1 190px', minWidth: 175, px: 1.75, py: 1.4, borderRadius: 2,
        backgroundColor: surface,
        border: `1px solid ${active ? alpha(valueTone, 0.55) : line}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color .15s ease, background-color .15s ease',
        '&:hover': onClick
          ? { borderColor: alpha(valueTone, 0.45), backgroundColor: alpha(colors.grey[100], 0.03) }
          : {},
        '&:focus-visible': { outline: `2px solid ${alpha(valueTone, 0.6)}`, outlineOffset: 2 },
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography
          fontSize="11px" fontWeight={700} color={colors.grey[400]}
          textTransform="uppercase" letterSpacing="0.5px"
        >
          {label}
        </Typography>
        <Icon sx={{ fontSize: 17, color: active ? valueTone : colors.grey[500] }} />
      </Box>
      <Typography
        variant="h3" fontWeight={800} mt={0.25} color={valueTone}
        sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.15 }}
      >
        {value}
      </Typography>
      <Typography fontSize="11px" color={colors.grey[400]}>{hint}</Typography>
    </Paper>
  );

  const movementRow = (movement) => {
    const tone = movement.delta < 0 ? toneColor('out') : toneColor('in');
    return (
      <Box key={movement.id} display="flex" alignItems="flex-start" mb={0.6}>
        <Box
          sx={{
            minWidth: 46, textAlign: 'center', flexShrink: 0, mr: 1, mt: '1px',
            px: 0.5, py: '1px', borderRadius: 1, fontSize: '13px', fontWeight: 800,
            fontVariantNumeric: 'tabular-nums',
            color: tone, backgroundColor: alpha(tone, 0.14),
          }}
        >
          {signed(movement.delta)}
        </Box>
        <Box minWidth={0}>
          <Typography fontSize="13px" color={colors.grey[100]} lineHeight={1.35}>
            {movement.partLabel}
          </Typography>
          <Box display="flex" alignItems="center" flexWrap="wrap" sx={{ columnGap: 0.75 }}>
            <Typography
              fontSize="12px" color={colors.grey[400]}
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {movement.quantityBefore} → <Box
                component="span"
                sx={{ fontWeight: 700, color: movement.quantityAfter < 0 ? toneColor('out') : colors.grey[300] }}
              >
                {movement.quantityAfter}
              </Box> in stock
            </Typography>
            {movement.quantityAfter < 0 && (
              <Typography fontSize="11px" fontWeight={700} color={toneColor('out')}>
                below zero
              </Typography>
            )}
            {movement.partCode && (
              <Typography fontSize="11px" color={colors.grey[500]}>{movement.partCode}</Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const stockCell = (entry) => {
    if (entry.movements.length === 0) {
      return (
        <Typography fontSize="12px" color={colors.grey[500]} fontStyle="italic">
          no stock change
        </Typography>
      );
    }

    const net = entry.movements.reduce((total, movement) => total + movement.delta, 0);
    const isOpen = !!expanded[entry.id];
    const first = entry.movements.slice(0, PARTS_SHOWN);
    const rest = entry.movements.slice(PARTS_SHOWN);
    const netTone = net < 0 ? toneColor('out') : toneColor('in');

    return (
      <Box>
        {entry.movements.length > 1 && (
          <Box
            display="inline-flex" alignItems="center" mb={0.75}
            sx={{
              px: 0.85, py: '2px', borderRadius: 1,
              backgroundColor: alpha(netTone, 0.12),
            }}
          >
            {net < 0
              ? <TrendingDownIcon sx={{ fontSize: 14, color: netTone, mr: 0.5 }} />
              : <TrendingUpIcon sx={{ fontSize: 14, color: netTone, mr: 0.5 }} />}
            <Typography
              fontSize="12px" fontWeight={800} color={netTone}
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {signed(net)} units
            </Typography>
            <Typography fontSize="12px" color={colors.grey[400]} ml={0.5}>
              across {entry.movements.length} parts
            </Typography>
          </Box>
        )}

        {first.map(movementRow)}

        <Collapse in={isOpen} unmountOnExit>
          <Box>{rest.map(movementRow)}</Box>
        </Collapse>

        {rest.length > 0 && (
          <Button
            size="small"
            onClick={() => setExpanded({ ...expanded, [entry.id]: !isOpen })}
            endIcon={
              <KeyboardArrowDownIcon
                sx={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s ease' }}
              />
            }
            sx={{
              mt: 0.25, px: 0.75, minHeight: 26, textTransform: 'none', fontSize: '12px',
              fontWeight: 700, color: colors.blueAccent[isDark ? 300 : 500],
              '&:hover': { backgroundColor: alpha(colors.blueAccent[500], 0.1) },
            }}
          >
            {isOpen ? 'Show fewer' : `${rest.length} more part${rest.length === 1 ? '' : 's'}`}
          </Button>
        )}
      </Box>
    );
  };

  const headCells = [
    { label: 'When', width: '11%' },
    { label: 'What happened', width: '33%' },
    { label: 'Stock change', width: '27%' },
    { label: 'Order', width: '15%' },
    { label: 'Who', width: '14%' },
  ];

  const pageStart = page * rowsPerPage;
  const paged = ordered.slice(pageStart, pageStart + rowsPerPage);

  // Days become bands with their own count and net movement, so "when" is answered by
  // where a row sits rather than by reading every timestamp.
  const days = [];
  paged.forEach((entry) => {
    const heading = dayHeading(entry.createdAt);
    let day = days[days.length - 1];
    if (!day || day.heading !== heading) {
      day = { heading, entries: [], net: 0, moved: false };
      days.push(day);
    }
    day.entries.push(entry);
    entry.movements.forEach((movement) => {
      day.net += movement.delta;
      day.moved = true;
    });
  });

  if (!canView) {
    return <AccessDenied pageName={"The activity log"} />;
  }

  return (
    <Box m={2}>
      {/* ---- title, and the two things you do to the whole list ---- */}
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h3" fontWeight={800} color={colors.grey[100]}>
            Activity Log
          </Typography>
          <Typography variant="body1" color={colors.grey[300]} mt={0.5}>
            One line for each thing somebody did, showing what they did, what it did to stock, and when.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {loadedAt && !loading && (
            <Typography fontSize="12px" color={colors.grey[400]} mr={0.5}>
              Updated {dayjs(loadedAt).format('h:mm a')}
            </Typography>
          )}
          {actionButton('Refresh', RefreshIcon, load, colors.blueAccent[700], loading)}
          {actionButton('Export CSV', FileDownloadOutlinedIcon, exportCsv, colors.greenAccent[700], ordered.length === 0)}
        </Stack>
      </Box>

      {/* ---- one card for every way of narrowing the list ---- */}
      <Paper
        elevation={0}
        sx={{ mt: 2, p: 1.5, borderRadius: 2, backgroundColor: surface, border: `1px solid ${line}` }}
      >
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          <Box
            display="flex" alignItems="center"
            sx={{
              flex: '1 1 240px', maxWidth: 380, height: CONTROL_HEIGHT, px: 1.25,
              borderRadius: 1, border: `1px solid ${line}`,
              transition: 'border-color .15s ease',
              '&:hover': { borderColor: alpha(colors.grey[100], 0.3) },
              '&:focus-within': { borderColor: colors.greenAccent[500] },
            }}
          >
            <SearchIcon sx={{ color: colors.grey[400], fontSize: 18, mr: 0.85, flexShrink: 0 }} />
            <InputBase
              placeholder="Search people, orders or parts"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setAppliedSearch(search.trim()); }}
              inputProps={{ 'aria-label': 'Search the activity log' }}
              sx={{
                flex: 1, fontSize: '13px', color: colors.grey[100],
                '& input::placeholder': { color: colors.grey[400], opacity: 1 },
              }}
            />
            {search && (
              <Tooltip title="Clear the search">
                <IconButton
                  size="small" onClick={() => setSearch('')} aria-label="Clear the search"
                  sx={{ p: 0.25, ml: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 16, color: colors.grey[400] }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Box display="flex" alignItems="center" gap={1} ml="auto">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={period}
              onChange={(e, next) => { if (next) setPeriod(next); }}
              aria-label="How far back to look"
              sx={{
                height: CONTROL_HEIGHT, p: '3px', gap: '2px', borderRadius: 1,
                backgroundColor: trackBg,
                border: `1px solid ${line}`,
                // MUI joins the segments with a -1px margin and squared inner corners.
                // A segmented control wants them separate and evenly rounded.
                '& .MuiToggleButtonGroup-firstButton, & .MuiToggleButtonGroup-middleButton, & .MuiToggleButtonGroup-lastButton': {
                  marginLeft: 0,
                  borderLeft: 0,
                  borderRadius: '3px !important',
                },
                '& .MuiToggleButtonGroup-grouped': {
                  height: '100%', m: 0, border: 0, borderRadius: '3px !important',
                  px: 1.5, py: 0, textTransform: 'none', fontSize: '13px', fontWeight: 600,
                  color: colors.grey[300],
                  '&:hover': {
                    color: colors.grey[100],
                    backgroundColor: alpha(colors.grey[100], 0.07),
                  },
                  '&.Mui-selected': {
                    color: colors.grey[100],
                    fontWeight: 700,
                    backgroundColor: thumbBg,
                    boxShadow: isDark
                      ? '0 1px 2px rgba(0, 0, 0, 0.45)'
                      : '0 1px 2px rgba(0, 0, 0, 0.14)',
                    '&:hover': { backgroundColor: thumbBg },
                  },
                },
              }}
            >
              {PERIODS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  <Tooltip title={option.label}><span>{option.short}</span></Tooltip>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Tooltip title={newestFirst ? 'Newest first. Click for oldest first' : 'Oldest first. Click for newest first'}>
              <Button
                onClick={() => setNewestFirst(!newestFirst)}
                startIcon={newestFirst ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                sx={{
                  height: CONTROL_HEIGHT, px: 1.5, py: 0, borderRadius: 1, textTransform: 'none',
                  fontSize: '13px', fontWeight: 600, color: colors.grey[300],
                  backgroundColor: trackBg, border: `1px solid ${line}`,
                  whiteSpace: 'nowrap',
                  '& .MuiButton-startIcon': { mr: 0.65 },
                  '& .MuiSvgIcon-root': { fontSize: 17 },
                  '&:hover': {
                    color: colors.grey[100],
                    backgroundColor: trackBg,
                    borderColor: alpha(colors.grey[100], 0.3),
                  },
                }}
              >
                {newestFirst ? 'Newest first' : 'Oldest first'}
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <Box
          display="flex" alignItems="center" flexWrap="wrap" mt={1.25}
          sx={{ borderTop: `1px solid ${line}`, pt: 0.25 }}
        >
          {VIEWS.map((option) => {
            const count = viewCounts[option.value] ?? 0;
            const selected = view === option.value;
            return (
              <Tooltip key={option.value || 'all'} title={option.label}>
                <Box
                  component="button"
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setView(option.value)}
                  sx={{
                    display: 'flex', alignItems: 'center', px: 1.25, py: 1,
                    appearance: 'none', background: 'none', border: 0, borderRadius: 0,
                    cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: '13px',
                    borderBottom: `2px solid ${selected ? colors.greenAccent[500] : 'transparent'}`,
                    color: selected ? colors.grey[100] : colors.grey[300],
                    transition: 'color .15s ease, border-color .15s ease',
                    '&:hover': {
                      color: colors.grey[100],
                      borderBottomColor: selected ? colors.greenAccent[500] : alpha(colors.grey[100], 0.25),
                    },
                  }}
                >
                  <Box component="span" sx={{ fontWeight: selected ? 700 : 600 }}>
                    {option.short}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75, fontSize: '12px', fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      color: count === 0 ? colors.grey[500] : colors.grey[400],
                    }}
                  >
                    {count}
                  </Box>
                </Box>
              </Tooltip>
            );
          })}

          <Box display="flex" alignItems="center" ml="auto" gap={0.5}>
            {appliedSearch && (
              <Chip
                size="small"
                label={`matching “${appliedSearch}”`}
                onDelete={() => { setSearch(''); setAppliedSearch(''); }}
                sx={{
                  height: 26, fontSize: '12px', color: colors.grey[200],
                  backgroundColor: alpha(colors.grey[100], 0.1),
                  '& .MuiChip-deleteIcon': { color: colors.grey[400], fontSize: 16 },
                }}
              />
            )}

            <Tooltip title="Only the actions worth checking: stock below zero, stock with no order behind it, or units taken twice">
              <Box
                component="button"
                type="button"
                aria-pressed={attentionOnly}
                onClick={() => setAttentionOnly(!attentionOnly)}
                sx={{
                  display: 'flex', alignItems: 'center', px: 1, py: 0.6, borderRadius: 1.5,
                  appearance: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: '13px', fontWeight: 600,
                  border: `1px solid ${attentionOnly ? alpha(toneColor('out'), 0.55) : 'transparent'}`,
                  backgroundColor: attentionOnly ? alpha(toneColor('out'), 0.12) : 'transparent',
                  color: attentionOnly ? colors.grey[100] : colors.grey[300],
                  '&:hover': { color: colors.grey[100], backgroundColor: alpha(toneColor('out'), 0.1) },
                }}
              >
                <WarningAmberIcon
                  sx={{
                    fontSize: 15, mr: 0.75,
                    color: attentionCount > 0 ? toneColor('out') : colors.grey[500],
                  }}
                />
                <Box component="span">Needs a look</Box>
                <Box
                  component="span"
                  sx={{
                    ml: 0.75, fontSize: '12px', fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    color: attentionCount === 0 ? colors.grey[500] : toneColor('out'),
                  }}
                >
                  {attentionCount}
                </Box>
              </Box>
            </Tooltip>

            {filtersTouched && (
              <Button
                size="small"
                startIcon={<FilterAltOffIcon />}
                onClick={clearFilters}
                sx={{
                  textTransform: 'none', fontSize: '12px', fontWeight: 600,
                  color: colors.grey[400],
                  '&:hover': { color: colors.grey[100], backgroundColor: alpha(colors.grey[100], 0.07) },
                }}
              >
                Clear filters
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* ---- the four numbers for whatever is on screen ---- */}
      <Box display="flex" flexWrap="wrap" gap={1.25} mt={1.5}>
        {tile({
          label: 'Things people did', value: summary.actions, hint: periodLabel.toLowerCase(),
          Icon: BoltOutlinedIcon, valueTone: colors.grey[100],
        })}
        {tile({
          label: 'Units out of stock', value: summary.unitsOut === 0 ? '0' : `-${summary.unitsOut}`,
          hint: 'taken off the shelf', Icon: TrendingDownIcon,
          valueTone: summary.unitsOut === 0 ? colors.grey[100] : toneColor('out'),
        })}
        {tile({
          label: 'Units back in stock', value: summary.unitsIn === 0 ? '0' : `+${summary.unitsIn}`,
          hint: 'put back on the shelf', Icon: TrendingUpIcon,
          valueTone: summary.unitsIn === 0 ? colors.grey[100] : toneColor('in'),
        })}
        {tile({
          label: 'Needs a look', value: summary.attention,
          hint: attentionOnly
            ? 'showing only these'
            : (attentionCount > 0 ? 'click to see only these' : 'nothing to check'),
          Icon: WarningAmberIcon,
          valueTone: summary.attention > 0 ? toneColor('out') : colors.grey[100],
          active: attentionOnly,
          onClick: attentionCount > 0 || attentionOnly
            ? () => setAttentionOnly(!attentionOnly)
            : undefined,
        })}
      </Box>

      {hitLimit && notice(toneColor('info'), InfoOutlinedIcon,
        `Showing the most recent ${FETCH_LIMIT} entries for this period. Pick a shorter period to see everything in it.`)}
      {error && notice(toneColor('out'), ErrorOutlineIcon, error)}

      {/* ---- the list itself ---- */}
      {loading ? (
        <Box
          display="flex" justifyContent="center" alignItems="center" minHeight={220}
          sx={{ '& .sk-folding-cube .sk-cube:before': { backgroundColor: cubeInk } }}
        >
          <div className="sk-folding-cube">
            <div className="sk-cube1 sk-cube"></div>
            <div className="sk-cube2 sk-cube"></div>
            <div className="sk-cube4 sk-cube"></div>
            <div className="sk-cube3 sk-cube"></div>
          </div>
        </Box>
      ) : filtered.length === 0 ? (
        <Box
          mt={2} p={5} textAlign="center"
          sx={{ backgroundColor: surface, borderRadius: 2, border: `1px dashed ${line}` }}
        >
          <SearchOffIcon sx={{ fontSize: 40, color: colors.grey[400] }} />
          <Typography variant="h5" fontWeight={700} color={colors.grey[200]} mt={1}>
            Nothing happened here
          </Typography>
          <Typography variant="body2" color={colors.grey[400]} mt={0.5}>
            No activity matches what you picked.
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center" mt={2}>
            {filtersTouched && actionButton('Clear filters', FilterAltOffIcon, clearFilters, colors.blueAccent[700])}
            {period !== 'all' && actionButton('Search all time', EventNoteOutlinedIcon, () => setPeriod('all'), colors.greenAccent[700])}
          </Stack>
        </Box>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              position: 'relative', mt: 1.5, borderRadius: 2, overflow: 'hidden',
              backgroundColor: surface, border: `1px solid ${line}`,
            }}
          >
            <TableContainer
              ref={scrollRef}
              sx={{ maxHeight: 'calc(100vh - 400px)', minHeight: 320 }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    {headCells.map((column) => (
                      <TableCell
                        key={column.label}
                        sx={{
                          width: column.width, backgroundColor: colors.blueAccent[700],
                          color: colors.grey[100], fontWeight: 800, fontSize: '11px',
                          textTransform: 'uppercase', letterSpacing: '0.6px',
                          borderBottom: 'none', py: 1.25,
                        }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {days.map((day) => [
                    <TableRow key={`day-${day.heading}`}>
                      <TableCell
                        colSpan={headCells.length}
                        sx={{ backgroundColor: band, borderBottom: `1px solid ${line}`, py: 0.85 }}
                      >
                        <Box display="flex" alignItems="center" flexWrap="wrap" sx={{ columnGap: 1 }}>
                          <EventNoteOutlinedIcon sx={{ fontSize: 15, color: colors.greenAccent[isDark ? 400 : 600] }} />
                          <Typography
                            fontSize="12px" fontWeight={800} letterSpacing="0.6px"
                            textTransform="uppercase" color={colors.greenAccent[isDark ? 400 : 600]}
                          >
                            {day.heading}
                          </Typography>
                          <Typography fontSize="11px" color={colors.grey[400]}>
                            {day.entries.length} action{day.entries.length === 1 ? '' : 's'}
                          </Typography>
                          {day.moved && (
                            <Typography
                              fontSize="11px" fontWeight={700}
                              color={day.net < 0 ? toneColor('out') : toneColor('in')}
                              sx={{ fontVariantNumeric: 'tabular-nums' }}
                            >
                              {signed(day.net)} units on the day
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>,

                    ...day.entries.map((entry) => {
                      const style = ACTION_STYLE[entry.action]
                        ?? { Icon: SwapHorizIcon, tone: 'info', word: entry.action };
                      const { Icon } = style;
                      const color = toneColor(style.tone);
                      const who = entry.actorName || '';

                      return (
                        <TableRow
                          key={entry.id}
                          hover
                          sx={{
                            backgroundColor: entry.needsAttention
                              ? alpha(colors.redAccent[500], isDark ? 0.12 : 0.08)
                              : 'inherit',
                            '& td': { borderColor: line, py: 1.25, verticalAlign: 'top' },
                          }}
                        >
                          {/* When */}
                          <TableCell sx={{ whiteSpace: 'nowrap', borderLeft: `3px solid ${entry.needsAttention ? toneColor('out') : color}` }}>
                            <Tooltip title={dayjs(entry.createdAt).format('dddd D MMMM YYYY, h:mm:ss a')}>
                              <Box>
                                <Typography fontWeight={700} fontSize="14px" color={colors.grey[100]}>
                                  {dayjs(entry.createdAt).format('h:mm a')}
                                </Typography>
                                <Typography fontSize="11px" color={colors.grey[400]}>
                                  {relativeTime(entry.createdAt)}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>

                          {/* What happened - only what the person did */}
                          <TableCell>
                            <Box display="flex" alignItems="flex-start">
                              <Tooltip title={style.word}>
                                <Box
                                  sx={{
                                    width: 30, height: 30, mr: 1.25, borderRadius: '9px',
                                    display: 'grid', placeItems: 'center', flexShrink: 0,
                                    backgroundColor: alpha(color, 0.16),
                                  }}
                                >
                                  <Icon sx={{ fontSize: 18, color }} />
                                </Box>
                              </Tooltip>
                              <Box minWidth={0}>
                                <Typography fontSize="14px" fontWeight={700} color={colors.grey[100]} lineHeight={1.4}>
                                  {entry.summary}
                                </Typography>
                                {entry.detail && (
                                  <Typography fontSize="12.5px" color={colors.grey[300]} mt={0.25} lineHeight={1.4}>
                                    {entry.detail}
                                  </Typography>
                                )}
                                {entry.needsAttention && (
                                  <Box
                                    display="flex" alignItems="flex-start" mt={0.75} p={0.85}
                                    sx={{
                                      borderRadius: 1.5,
                                      backgroundColor: alpha(toneColor('out'), 0.12),
                                      border: `1px solid ${alpha(toneColor('out'), 0.35)}`,
                                    }}
                                  >
                                    <WarningAmberIcon sx={{ fontSize: 15, color: toneColor('out'), mr: 0.75, mt: '1px', flexShrink: 0 }} />
                                    <Box>
                                      <Typography fontSize="11px" fontWeight={800} textTransform="uppercase" letterSpacing="0.5px" color={toneColor('out')}>
                                        Needs a look
                                      </Typography>
                                      <Typography fontSize="12.5px" color={colors.grey[100]} lineHeight={1.4}>
                                        {entry.warning
                                          || 'Stock moved with nothing to tie it to, or it went below zero.'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Stock change - what came off the shelf and what is left */}
                          <TableCell>{stockCell(entry)}</TableCell>

                          {/* Order */}
                          <TableCell>
                            {entry.quotationNumber ? (
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-block', px: 0.85, py: '2px', borderRadius: 1,
                                  fontSize: '13px', fontWeight: 800, color: colors.grey[100],
                                  backgroundColor: alpha(colors.blueAccent[500], 0.16),
                                }}
                              >
                                {entry.quotationNumber}
                              </Box>
                            ) : (
                              <Typography fontSize="12px" color={colors.grey[500]} fontStyle="italic">
                                no order
                              </Typography>
                            )}
                            {entry.isRevision && entry.baseOrderNumber && (
                              <Typography fontSize="11px" color={colors.blueAccent[isDark ? 300 : 500]} mt={0.4}>
                                revision of {entry.baseOrderNumber}
                              </Typography>
                            )}
                            {entry.clientName && (
                              <Typography fontSize="12px" color={colors.grey[400]} mt={0.25}>
                                {entry.clientName}
                              </Typography>
                            )}
                          </TableCell>

                          {/* Who */}
                          <TableCell sx={{ wordBreak: 'break-word' }}>
                            {who ? (
                              <Box display="flex" alignItems="center">
                                <Avatar
                                  sx={{
                                    width: 26, height: 26, mr: 1, fontSize: '11px', fontWeight: 800,
                                    color: colors.grey[100],
                                    backgroundColor: alpha(
                                      colors.blueAccent[[300, 400, 500, 600][nameShade(who) % 4]],
                                      isDark ? 0.35 : 0.5,
                                    ),
                                  }}
                                >
                                  {initialsOf(who) || '?'}
                                </Avatar>
                                <Typography fontSize="13px" color={colors.grey[100]}>{who}</Typography>
                              </Box>
                            ) : (
                              <Tooltip title="No user was recorded. This came from a script or straight from the database.">
                                <Box display="flex" alignItems="center">
                                  <PersonOutlineIcon sx={{ fontSize: 18, color: colors.grey[500], mr: 0.75 }} />
                                  <Typography fontSize="12px" color={colors.grey[500]} fontStyle="italic">
                                    not recorded
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    }),
                  ])}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={ordered.length}
              page={page}
              onPageChange={(event, next) => goToPage(next)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                goToPage(0);
              }}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
              showFirstButton
              showLastButton
              sx={{
                borderTop: `1px solid ${line}`,
                color: colors.grey[300],
                '& .MuiTablePagination-toolbar': { minHeight: 48, flexWrap: 'wrap' },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '13px', color: colors.grey[300], mb: 0,
                },
                '& .MuiTablePagination-select': { fontSize: '13px', fontWeight: 700, color: colors.grey[100] },
                '& .MuiTablePagination-actions .MuiIconButton-root': { color: colors.grey[200] },
                '& .MuiTablePagination-actions .MuiIconButton-root:hover': {
                  backgroundColor: alpha(colors.blueAccent[500], 0.14),
                },
                '& .MuiTablePagination-actions .Mui-disabled': { color: alpha(colors.grey[100], 0.25) },
                '& .MuiSelect-icon': { color: colors.grey[300] },
              }}
            />
          </Paper>

          {ordered.length !== entries.length && (
            <Typography fontSize="12px" color={colors.grey[400]} mt={1} textAlign="center">
              Filtered down from {entries.length} {entries.length === 1 ? 'action' : 'actions'} in {periodLabel.toLowerCase()}.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default ActivityLog;
